/* eslint-disable max-lines */
import {
  IconExternalLink,
  IconEye,
  IconFileSpreadsheet,
  IconFileTypePdf,
  IconFiles,
  IconPlugConnected,
  IconReload,
  IconUserCheck,
} from '@tabler/icons-react';
import {
  ApplicationSubmissionMethod,
  CreateApplicationErrorDto,
  CreateApplicationResultDto,
  CreateAutomaticApplicationDto,
  isDefined,
} from '@zorro/clients';
import { APP_ROUTES, responseErrorToStrings } from '@zorro/common-utils';
import { Alert } from '@zorro/design-system/v2/Alert';
import { Badge } from '@zorro/design-system/v2/Badge';
import { Button } from '@zorro/design-system/v2/Button';
import { RadioCard, RadioGroup } from '@zorro/design-system/v2/Radio';
import { Select } from '@zorro/design-system/v2/Select';
import { formatDateTimeEnLocaleFromUtc } from '@zorro/shared/formatters';
import { Grid, Group, Icon, Stack, Text } from '@zorro/web/design-system';
import { useAnalytics } from '@zorro/web/shared-ui';
import {
  formatTemplateName,
  useAppRouter,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { ComponentType, useState } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';

import { CarrierApplicationModalStepType } from '../../CarrierApplicationModal';
import { CarrierApplicationSubmitFormFields } from '../CarrierCompleteStepUtils';
import { BrokerAttestationGate } from '../useBrokerAttestation';
import { useCarrierDocumentDownload } from '../useCarrierDocumentDownload';
import { CarrierBrokerAttestationSection } from './CarrierBrokerAttestationSection';
import { CarrierSpecialEnrollmentDocumentsSection } from './CarrierSpecialEnrollmentDocumentsSection';

const TECHNICAL_SUBMIT_ERROR_MESSAGE =
  "We couldn't submit this application. Try again shortly, or use another method";

const TECHNICAL_ERROR_CLASS = 'technical';
const INPUT_ERROR_CLASS = 'input_error';
// Lowercase `error_class` event vocabulary, keyed by the backend enum so a class it
// adds can't reach the events unmapped.
const EVENT_ERROR_CLASS: Record<CreateApplicationErrorDto.errorClass, string> =
  {
    [CreateApplicationErrorDto.errorClass.TECHNICAL]: TECHNICAL_ERROR_CLASS,
    [CreateApplicationErrorDto.errorClass.INPUT_ERROR]: INPUT_ERROR_CLASS,
  };

type GetRecommendedSubmissionMethodProps = {
  isEnrollConnectOffered: boolean;
  isDeeplinkOffered: boolean;
  isPdfOffered: boolean;
};

const getRecommendedSubmissionMethod = ({
  isEnrollConnectOffered,
  isDeeplinkOffered,
  isPdfOffered,
}: GetRecommendedSubmissionMethodProps):
  ApplicationSubmissionMethod | undefined => {
  if (isEnrollConnectOffered) {
    return ApplicationSubmissionMethod.ENROLL_CONNECT;
  }

  if (isDeeplinkOffered) {
    return ApplicationSubmissionMethod.DEEPLINK;
  }

  if (isPdfOffered) {
    return ApplicationSubmissionMethod.MANUAL;
  }

  return undefined;
};

type SubmissionMethodCard = {
  value: ApplicationSubmissionMethod;
  label: string;
  icon: ComponentType<{ size?: number; stroke?: number }>;
};

type Props = {
  employeeId: string;
  employerId: string;
  onboardingPeriodId: string;
  benefitId?: string;
  shouldShowPlanGeneratePdf: boolean;
  shouldShowHealthSherpa: boolean;
  shouldShowEnrollConnect: boolean;
  onDetailsReviewOpen: () => void;
  enrollConnectResult: CreateApplicationResultDto | null;
  submittedApplication: Pick<
    CreateApplicationResultDto,
    'applicationId' | 'submittedAt' | 'payment'
  > | null;
  onEnrollConnectResult: (result: CreateApplicationResultDto) => void;
  persistProofOfConsentDocuments: () => Promise<boolean>;
  persistSpecialEnrollmentDocuments: () => Promise<CreateAutomaticApplicationDto | null>;
  submissionForm: UseFormReturn<CarrierApplicationSubmitFormFields>;
  // Owned by the step so the submit lock and this section read one instance.
  brokerAttestationGate: BrokerAttestationGate;
};

export const CarrierSubmissionMethodSection = ({
  employeeId,
  employerId,
  onboardingPeriodId,
  benefitId,
  shouldShowPlanGeneratePdf,
  shouldShowHealthSherpa,
  shouldShowEnrollConnect,
  onDetailsReviewOpen,
  enrollConnectResult,
  submittedApplication,
  onEnrollConnectResult,
  persistProofOfConsentDocuments,
  persistSpecialEnrollmentDocuments,
  submissionForm,
  brokerAttestationGate,
}: Props) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [submitRequestErrors, setSubmitRequestErrors] = useState<string[]>([]);
  // Transport failures replace the result's errors; the CREATED_WITH_ERRORS copy
  // is derived from the lifted result so it survives step navigation.
  const enrollConnectErrorMessages =
    submitRequestErrors.length > 0
      ? submitRequestErrors
      : enrollConnectResult?.status ===
          CreateApplicationResultDto.status.CREATED_WITH_ERRORS
        ? [
            ...new Set(
              enrollConnectResult.errors.map(
                (error) =>
                  error.displayMessage ?? TECHNICAL_SUBMIT_ERROR_MESSAGE
              )
            ),
          ]
        : [];
  const selectedMethod = useWatch({
    control: submissionForm.control,
    name: 'submissionMethod',
  });
  // Self-enroll / bulk-CSV can't be chosen here, but an already-submitted
  // application may carry one - show it so the stored value stays visible.
  const storedSubmissionMethod =
    submissionForm.formState.defaultValues?.submissionMethod;

  const { sendEvent } = useAnalytics();
  const { navigateToRoute } = useAppRouter();
  const { onDownloadPdf, onContinueHealthSherpa } = useCarrierDocumentDownload({
    employeeId,
    onboardingPeriodId,
    benefitId,
  });
  const { data: templates } = useMonolithQuery({
    method: 'formsFillerControllerGetTemplates',
    params: [],
  });
  const { data: availableSubmissionMethods } = useMonolithQuery({
    method: 'applicationsControllerGetAvailableSubmissionMethods',
    params: benefitId && [employeeId, benefitId],
  });
  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });
  const {
    tryMutate: submitEnrollConnect,
    isPending: isSubmittingEnrollConnect,
  } = useMonolithMutation({
    method: 'applicationsControllerCreateAutomaticApplication',
    shouldReloadPage: false,
    shouldInvalidateQueries: false,
    shouldShowLoadingOverlay: false,
    // Failures render inline below the button, not as a toast.
    displayErrorNotification: false,
  });
  const {
    isAttestationGateActive,
    isBrokerAttestationAccepted,
    isMethodActionVisible,
    brokerAttestation,
    electionSubmittedNotice,
  } = brokerAttestationGate;
  const isMethodAvailable = (method: ApplicationSubmissionMethod) =>
    availableSubmissionMethods?.methods.includes(method) ?? false;
  // Only a mid-year start has a reason, and only an unsubmitted application can still
  // change what proof is sent with it.
  const specialEnrollment = availableSubmissionMethods?.specialEnrollment;
  const shouldShowSpecialEnrollmentDocuments =
    selectedMethod === ApplicationSubmissionMethod.ENROLL_CONNECT &&
    !isDefined(submittedApplication);
  // A stored method stays visible even when it's no longer offered,
  // but only a currently offered method can be recommended.
  const isPdfOffered =
    shouldShowPlanGeneratePdf &&
    isMethodAvailable(ApplicationSubmissionMethod.PDF);
  const shouldShowPdfOption =
    isPdfOffered || storedSubmissionMethod === ApplicationSubmissionMethod.PDF;
  const isDeeplinkOffered =
    shouldShowHealthSherpa &&
    isMethodAvailable(ApplicationSubmissionMethod.DEEPLINK);
  const shouldShowDeeplinkOption =
    isDeeplinkOffered ||
    storedSubmissionMethod === ApplicationSubmissionMethod.DEEPLINK;
  const isEnrollConnectOffered =
    shouldShowEnrollConnect &&
    isMethodAvailable(ApplicationSubmissionMethod.ENROLL_CONNECT);
  const shouldShowEnrollConnectOption =
    isEnrollConnectOffered ||
    storedSubmissionMethod === ApplicationSubmissionMethod.ENROLL_CONNECT;

  const recommendedSubmissionMethod = getRecommendedSubmissionMethod({
    isEnrollConnectOffered,
    isDeeplinkOffered,
    isPdfOffered,
  });

  const submissionMethodCards: SubmissionMethodCard[] = [
    ...(shouldShowEnrollConnectOption
      ? [
          {
            value: ApplicationSubmissionMethod.ENROLL_CONNECT,
            label: 'Auto-submit application',
            icon: IconPlugConnected,
          },
        ]
      : []),
    ...(shouldShowDeeplinkOption
      ? [
          {
            value: ApplicationSubmissionMethod.DEEPLINK,
            label: 'HealthSherpa Deeplink',
            icon: IconExternalLink,
          },
        ]
      : []),
    ...(shouldShowPdfOption
      ? [
          {
            value: ApplicationSubmissionMethod.PDF,
            label: 'PDF',
            icon: IconFileTypePdf,
          },
        ]
      : []),
    {
      // Copy only — the stored enum value stays MANUAL (no migration).
      value: ApplicationSubmissionMethod.MANUAL,
      label: 'Broker Portal',
      icon: IconFiles,
    },
    ...(storedSubmissionMethod === ApplicationSubmissionMethod.SELF_ENROLLED
      ? [
          {
            value: ApplicationSubmissionMethod.SELF_ENROLLED,
            label: 'Self-enrolled',
            icon: IconUserCheck,
          },
        ]
      : []),
    ...(storedSubmissionMethod === ApplicationSubmissionMethod.BULK_CSV
      ? [
          {
            value: ApplicationSubmissionMethod.BULK_CSV,
            label: 'Bulk CSV',
            icon: IconFileSpreadsheet,
          },
        ]
      : []),
  ];

  const handleOpenReview = () => {
    void sendEvent('click_view_application_details', {
      onboarding_period_id: onboardingPeriodId,
      ...(selectedMethod && { application_method: selectedMethod }),
      employer_id: employerId,
    });
    navigateToRoute(
      'OPS_EMPLOYEE_PLANS_TAB',
      [{ employerId, employeeId, onboardingPeriodId }],
      {
        step: CarrierApplicationModalStepType.REVIEW,
        ...(benefitId && { benefitId }),
      }
    );
    onDetailsReviewOpen();
  };

  // Side action, never a submit: opens the Zorro-origin Copilot launch page in a
  // new tab. The launch page (not the carrier URL) is what carries the ids the
  // extension picks up, so the radio selection and this step's form state are
  // untouched.
  const handleOpenCopilotLaunch = () => {
    if (!benefitId) return;

    void sendEvent('click_open_enrollment_copilot', {
      onboarding_period_id: onboardingPeriodId,
      employer_id: employerId,
    });
    // Named per benefit rather than '_blank': a second click on the same benefit
    // reuses and focuses that tab instead of leaving two live launch tabs for one
    // enrollment (the operator would then have to guess which is current). Other
    // benefits still get their own tab, so working several at once is unaffected.
    // Safe to keep the handle for focus() — the launch page is same-origin.
    window
      .open(
        APP_ROUTES.ENROLLMENT_COPILOT_LAUNCH({ benefitId, employeeId }),
        `zorro-copilot-${benefitId}`
      )
      ?.focus();
  };

  const handleContinueHealthSherpa = async () => {
    if (!(await persistProofOfConsentDocuments())) return;

    await onContinueHealthSherpa();
  };

  const handleSubmitEnrollConnect = async () => {
    if (!benefitId) return;

    if (!(await persistProofOfConsentDocuments())) return;

    // The picked proof is saved on the enrollment first, so the submission can reference it
    // by id (and the files stay reusable on a retry).
    const sepProof = await persistSpecialEnrollmentDocuments();
    if (!sepProof) return;

    const submitResult = await submitEnrollConnect([
      employeeId,
      benefitId,
      sepProof,
    ]);

    if (submitResult.isErr()) {
      // A 4xx is our own validation (actionable message, e.g. a missing phone number);
      // 5xx covers upstream/server failures, HealthSherpa API errors included (502).
      const isServerFailure = submitResult.error.status >= 500;
      setSubmitRequestErrors(
        isServerFailure
          ? [TECHNICAL_SUBMIT_ERROR_MESSAGE]
          : responseErrorToStrings(submitResult.error)
      );
      void sendEvent('enrollconnect_submit_failed', {
        onboarding_period_id: onboardingPeriodId,
        employer_id: employerId,
        errors: [
          {
            error_code: 'request_failed',
            error_class: isServerFailure
              ? TECHNICAL_ERROR_CLASS
              : INPUT_ERROR_CLASS,
            http_status: submitResult.error.status,
          },
        ],
      });
      return;
    }

    const application = submitResult.value;
    const hasSubmitErrors =
      application.status ===
      CreateApplicationResultDto.status.CREATED_WITH_ERRORS;
    setSubmitRequestErrors([]);
    onEnrollConnectResult(application);
    void sendEvent('click_submit_enroll_connect', {
      onboarding_period_id: onboardingPeriodId,
      employer_id: employerId,
      submission_status: application.status,
    });
    if (hasSubmitErrors) {
      void sendEvent('enrollconnect_submit_failed', {
        onboarding_period_id: onboardingPeriodId,
        employer_id: employerId,
        errors: application.errors.map((error) => ({
          error_code: error.code,
          error_class: EVENT_ERROR_CLASS[error.errorClass],
        })),
      });
    }
  };

  return (
    <Stack my="xl" gap="2rem">
      <Group justify="space-between" align="center" wrap="nowrap">
        <Stack gap="xxs">
          <Text size="md" fw={600}>
            Submit application
          </Text>
          <Text size="sm" fw={400} c="var(--color-text-default-secondary)">
            Available application options based on the carrier and state
          </Text>
        </Stack>
        <Button
          variant="secondary"
          size="sm"
          leftSection={<Icon icon={IconEye} />}
          onClick={handleOpenReview}
        >
          View application details
        </Button>
      </Group>
      <Stack gap="md">
        <Controller
          control={submissionForm.control}
          name="submissionMethod"
          render={({ field }) => (
            <RadioGroup
              value={field.value ?? null}
              onChange={(value) => {
                field.onChange(value);
                void sendEvent('change_submission_method', {
                  onboarding_period_id: onboardingPeriodId,
                  selected_method: value,
                });
              }}
            >
              <Grid gap="md">
                {submissionMethodCards.map(
                  ({ value, label, icon: IconComponent }) => (
                    <Grid.Col span={6} key={value}>
                      <RadioCard
                        size="md"
                        icon={<IconComponent />}
                        iconColor="indigo"
                        value={value}
                        checked={value === field.value}
                        label={label}
                        rightContent={
                          value === recommendedSubmissionMethod && (
                            <Badge variant="simple" color="indigo">
                              Recommended
                            </Badge>
                          )
                        }
                      />
                    </Grid.Col>
                  )
                )}
              </Grid>
            </RadioGroup>
          )}
        />

        {shouldShowSpecialEnrollmentDocuments && specialEnrollment && (
          <CarrierSpecialEnrollmentDocumentsSection
            employeeId={employeeId}
            onboardingPeriodId={onboardingPeriodId}
            onboardingPeriodEffectiveFrom={onboardingPeriod?.effectiveFrom}
            specialEnrollment={specialEnrollment}
            qualifyingLifeEvent={onboardingPeriod?.qualifyingLifeEvent}
            documents={onboardingPeriod?.benefitDocuments ?? []}
            submissionForm={submissionForm}
          />
        )}

        <CarrierBrokerAttestationSection
          employeeId={employeeId}
          onboardingPeriodId={onboardingPeriodId}
          isVisible={isAttestationGateActive}
          isAccepted={isBrokerAttestationAccepted}
          brokerAttestation={brokerAttestation}
          electionSubmittedNotice={electionSubmittedNotice}
          submissionForm={submissionForm}
        />

        {selectedMethod === ApplicationSubmissionMethod.MANUAL && (
          <Stack gap="sm">
            <Text size="sm" c="var(--color-text-default-secondary)">
              Enter these details into the carrier’s online form to submit, or
              let the Zorro Copilot browser extension fill them for you
            </Text>
            <Button
              w="min-content"
              rightSection={<IconExternalLink size={16} />}
              onClick={handleOpenCopilotLaunch}
              disabled={!benefitId}
            >
              Open with Zorro Copilot
            </Button>
          </Stack>
        )}

        {selectedMethod === ApplicationSubmissionMethod.PDF &&
          shouldShowPdfOption && (
            <Stack gap="xs">
              <Text size="sm" c="var(--color-text-default-secondary)">
                Download the auto-filled application PDF, review it carefully,
                and submit it to the carrier
              </Text>
              <Grid gap="xs" align="flex-start">
                <Grid.Col span={4}>
                  <Select
                    placeholder="Select template"
                    data={templates?.map((template) => ({
                      value: template.eid,
                      label: formatTemplateName(template.title),
                    }))}
                    onChange={setSelectedTemplate}
                    mb="xs"
                  />
                </Grid.Col>
                <Grid.Col span="content">
                  <Button
                    variant="primary"
                    onClick={() => onDownloadPdf(selectedTemplate)}
                    disabled={!selectedTemplate}
                  >
                    Download PDF
                  </Button>
                </Grid.Col>
              </Grid>
            </Stack>
          )}

        {selectedMethod === ApplicationSubmissionMethod.DEEPLINK &&
          shouldShowDeeplinkOption &&
          isMethodActionVisible && (
            <Stack gap="sm">
              <Text size="sm" c="var(--color-text-default-secondary)">
                Click below to open the pre-filled application form in
                HealthSherpa
              </Text>
              <Button
                w="min-content"
                rightSection={<IconExternalLink size={16} />}
                onClick={handleContinueHealthSherpa}
              >
                Open HealthSherpa Deeplink
              </Button>
            </Stack>
          )}

        {selectedMethod === ApplicationSubmissionMethod.ENROLL_CONNECT &&
          shouldShowEnrollConnectOption &&
          isMethodActionVisible && (
            <Stack gap="sm">
              <Text size="sm" c="var(--color-text-default-secondary)">
                The application will be submitted to the carrier through
                HealthSherpa EnrollConnect
              </Text>
              {!isDefined(submittedApplication) &&
                enrollConnectErrorMessages.length === 0 && (
                  <Button
                    w="min-content"
                    onClick={handleSubmitEnrollConnect}
                    loading={isSubmittingEnrollConnect}
                    disabled={!benefitId}
                  >
                    Auto-submit application
                  </Button>
                )}
              {isDefined(submittedApplication) && (
                <Alert
                  variant="success"
                  title="Successfully submitted via HealthSherpa EnrollConnect"
                >
                  <Stack gap="xxs">
                    <Text size="sm">
                      HealthSherpa application ID:{' '}
                      {submittedApplication.applicationId}
                    </Text>
                    {submittedApplication.submittedAt && (
                      <Text size="sm">
                        Submitted at:{' '}
                        {formatDateTimeEnLocaleFromUtc(
                          submittedApplication.submittedAt
                        )}
                      </Text>
                    )}
                    {submittedApplication.payment && (
                      <Text size="sm">
                        ACH payment set up from the member&apos;s Zorro Pay
                        account ({submittedApplication.payment.maskedAccount}):
                        initial premium payment
                        {isDefined(submittedApplication.payment.withdrawDay) &&
                          ` + monthly auto-pay on day ${submittedApplication.payment.withdrawDay}`}
                      </Text>
                    )}
                  </Stack>
                </Alert>
              )}
              {enrollConnectErrorMessages.length > 0 && (
                <Alert variant="error" title="Something went wrong">
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Stack gap="xxs">
                      {enrollConnectErrorMessages.map((message) => (
                        <Text size="sm" key={message}>
                          {message}
                        </Text>
                      ))}
                    </Stack>
                    <Button
                      variant="tertiary"
                      size="md"
                      leftSection={<Icon icon={IconReload} />}
                      onClick={handleSubmitEnrollConnect}
                      loading={isSubmittingEnrollConnect}
                    >
                      Retry
                    </Button>
                  </Group>
                </Alert>
              )}
            </Stack>
          )}
      </Stack>
    </Stack>
  );
};
